import { type Bar, type LibrarySymbolInfo, type ResolutionString, type SubscribeBarsCallback } from 'charting_library';
import { debounce } from 'es-toolkit/function';

import { type IMediator } from '#/core/mediator';

import { MessageListenerHandlerKey } from '#/services/message-listener/message-listener.types';

import { getTimeRangeOfTickByTimeframe } from '#/shared/utils/get-time-range-of-tick-by-timeframe';
import { isTickPayload } from '#/shared/utils/is-tick-payload';
import { isTickTimeframe } from '#/shared/utils/is-tick-timeframe';
import { safeJsonParse } from '#/shared/utils/safe-json-parse';

import { type TickPayload } from '#/types/messages';

import { SERVICES_TYPES } from '#/services-types';

import { type ILogService } from '../log';
import { type IMessageListenerService } from '../message-listener';

import { type IRealtimeBarUpdateService, type LastBarCacheKey, type Subscription } from './realtime-bar-update.types';

export class RealtimeBarUpdateService implements IRealtimeBarUpdateService {
  readonly #serviceName = 'RealtimeBarUpdateService';

  get #logService() {
    return this._mediator.get<ILogService>(SERVICES_TYPES.LogService);
  }

  get #messageListenerService() {
    return this._mediator.get<IMessageListenerService>(SERVICES_TYPES.MessageListenerService);
  }

  #LOG_TICKS_UPDATE = true;

  #onTick = (messagePayload: unknown) => {
    if (this.#subscriptions.length === 0) return;

    if (document.visibilityState !== 'visible') return;

    if (!isTickPayload(messagePayload)) return;

    const { data: tick } = messagePayload;

    this.#LOG_TICKS_UPDATE &&
      this.#logService.info(this.#serviceName, `Received tick data from parent app:`, messagePayload);

    const { symbol: tickSymbol } = tick;

    for (const subscription of this.#subscriptions) {
      if (subscription.symbol !== tickSymbol) {
        this.#logService.warn(
          this.#serviceName,
          // eslint-disable-next-line max-len
          `Mismatch subscription and payload symbols, subscriptionSymbol - '${subscription.symbol}', tickSymbol - '${tickSymbol}'.`
        );

        continue;
      }

      this.#processTickForSubscription(tick, subscription);
    }
  };

  #processTickForSubscription(tick: TickPayload['data'], subscription: Subscription) {
    const subscriptionSymbol = subscription.symbol;
    const subscriptionTimeframe = subscription.resolution;

    const lastBarBySymbolAndResolution = this.getLastBarCache(`${subscriptionSymbol}:${subscriptionTimeframe}`);

    if (!lastBarBySymbolAndResolution) {
      this.#logService.warn(this.#serviceName, 'No last bar for symbol and resolution in cache for subscription.');

      return;
    }

    const { time: tickTimestamp } = tick;

    if (isTickTimeframe(subscriptionTimeframe)) {
      const { bid: tickBid, ask: tickAsk } = tick;

      const bar: Bar = {
        time: tickTimestamp.raw,
        open: tickAsk,
        close: tickBid,
        low: tickAsk,
        high: tickBid,
      };

      this.#LOG_TICKS_UPDATE &&
        this.#logService.info(
          this.#serviceName,
          // eslint-disable-next-line max-len
          `Update bid and ask charts for symbol '${subscriptionSymbol}' and timeframe '${subscription.resolution}' with data:`,
          bar
        );

      this.setLastBarCache(`${subscriptionSymbol}:${subscriptionTimeframe}`, bar);

      subscription.newDataCallback(bar);

      return;
    }

    const tickTimeRange = getTimeRangeOfTickByTimeframe({
      tickTimestamp: tickTimestamp.raw,
      timeframe: subscriptionTimeframe,
    });

    if (!tickTimeRange) {
      this.#logService.warn(this.#serviceName, 'Cannot get tick time range.');

      return;
    }

    const { bid: tickBid } = tick;

    const bar = {} as Bar;

    if (
      lastBarBySymbolAndResolution.time >= tickTimeRange.start &&
      lastBarBySymbolAndResolution.time <= tickTimeRange.end
    ) {
      bar.time = lastBarBySymbolAndResolution.time;

      bar.open = lastBarBySymbolAndResolution.open;
      bar.high = Math.max(lastBarBySymbolAndResolution.high, tickBid);
      bar.low = Math.min(lastBarBySymbolAndResolution.low, tickBid);
      bar.close = tickBid;

      this.#LOG_TICKS_UPDATE &&
        this.#logService.info(
          this.#serviceName,
          `Update exist bar for symbol '${subscriptionSymbol}' and timeframe '${subscription.resolution}' with data:`,
          bar
        );
    } else {
      bar.time = tickTimeRange.start;

      bar.open = tickBid;
      bar.high = tickBid;
      bar.low = tickBid;
      bar.close = tickBid;

      this.#LOG_TICKS_UPDATE &&
        this.#logService.info(
          this.#serviceName,
          `Add new bar for symbol '${subscriptionSymbol}' and timeframe '${subscription.resolution}' with data:`,
          bar
        );
    }

    this.setLastBarCache(`${subscriptionSymbol}:${subscriptionTimeframe}`, bar);

    subscription.newDataCallback(bar);
  }

  #lastBarsCache = new Map<LastBarCacheKey, string>();

  #subscriptions: Subscription[] = [];

  constructor(private readonly _mediator: IMediator) {
    this.#logService.info(this.#serviceName, 'Instance was created.');
    this.#messageListenerService.setHandler('tickMessage', MessageListenerHandlerKey.Tick, debounce(this.#onTick, 100));
  }

  setLastBarCache(key: LastBarCacheKey, bar: Bar) {
    // ? Need create copy of bar for cached bar to be immutable from library
    this.#lastBarsCache.set(key, JSON.stringify(bar));
  }

  getLastBarCache(key: LastBarCacheKey) {
    return safeJsonParse<Bar>(this.#lastBarsCache.get(key));
  }

  subscribe({
    symbolInfo,
    resolution,
    newDataCallback,
  }: {
    symbolInfo: LibrarySymbolInfo;
    resolution: ResolutionString;
    newDataCallback: SubscribeBarsCallback;
  }): void {
    this.#logService.info(this.#serviceName, `Add new subscribed for '${symbolInfo.name}' - '${resolution}'.`);

    this.#subscriptions.push({
      symbol: symbolInfo.name,
      resolution: resolution,
      newDataCallback,
    });
  }

  unsubscribe(listenerGuid: string) {
    this.#logService.info(this.#serviceName, `Unsubscribed for '${listenerGuid}'.`);

    const [symbol, resolution] = listenerGuid.split('_#_');

    this.#subscriptions = this.#subscriptions.filter(
      (subscription) => !(subscription.symbol === symbol && subscription.resolution === resolution)
    );
  }
}
